import { FieldValues } from 'react-hook-form';

import { AutocompleteFormFieldProps } from '@/components/shared/components/NewContextForms/AutocompleteFormField';
import { AutocompleteFormField } from '@/components/shared/components/NewContextForms/AutocompleteFormField2';
import { useNationalities } from '@/components/shared/components/NewContextForms/AutocompleteNationalityFormField/hooks/useNationalities';
import { countryToFlag } from '@/components/shared/components/NewContextForms/PhoneFormDoubleField/utils/countryToFlag';

export interface AutocompleteNationalityFormFieldProps<TFieldValues extends FieldValues, TOption>
  extends Partial<Omit<AutocompleteFormFieldProps<TFieldValues, TOption>, 'name'>> {
  name: string;
  useAlpha2?: boolean;
}

export const AutocompleteNationalityFormField = <TFieldValues extends FieldValues, TOption>({
  useAlpha2 = false,
  ...props
}: AutocompleteNationalityFormFieldProps<TFieldValues, TOption>) => {
  const { nationalities } = useNationalities();

  const valueKey = useAlpha2 ? 'alpha2' : 'value';
  const optionList = nationalities.map((nationality) => nationality?.[valueKey]);
  const getOption = (value: string | undefined) =>
    nationalities.find((nationality) => nationality?.[valueKey] === value);

  const getInputDisplay = (value: string | undefined) => getOption(value)?.display ?? value ?? '';
  const getListDisplay = (value: string | undefined) => {
    const option = getOption(value);
    return option?.alpha2 ? `${countryToFlag(option.alpha2)} ${option.display}` : option?.display ?? value ?? '';
  };
  const getSearchValue = (value: string | undefined) => getOption(value)?.display ?? value ?? '';

  return (
    <AutocompleteFormField
      {...props}
      {...(props.value ? { value: getOption(props.value as string)?.display || null } : {})}
      optionList={optionList}
      getInputDisplay={getInputDisplay}
      getListDisplay={getListDisplay}
      getSearchValue={getSearchValue}
    />
  );
};
